/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { AiManagementState } from 'store/modules/ai_management_store/types';
import { ProfileState } from 'store/modules/profile_store/types';
import { RecommendedTask } from 'services/actions/ai_management_tasks';
import { formatDate } from 'services/ai_management_dates';
import { Container } from './styles';

export interface SuggestionDraft {
  key: string;
  title: string;
  selected: boolean;
  responsibleId: string;
  doneDate: string;
  doneTime: string;
  fatalDate: string;
  fatalTime: string;
  fatalNotApplicable: boolean;
}

const toDraft = (
  suggestion: RecommendedTask,
  index: number,
  responsibleId: string,
): SuggestionDraft => ({
  key: `${index}-${suggestion.title}`,
  title: suggestion.title,
  selected: true,
  responsibleId,
  doneDate: suggestion.start_on ?? '',
  doneTime: '',
  fatalDate: suggestion.deadline_on ?? '',
  fatalTime: suggestion.deadline_time ?? '',
  fatalNotApplicable: !suggestion.deadline_on,
});

const formatDeadline = (date: string, time: string) => {
  if (!date) return 'Sem prazo';
  const day = formatDate(date);
  return time ? `${day} às ${time}` : day;
};

interface AiManagementTaskSuggestionsProps {
  suggestions: RecommendedTask[];
  saving: boolean;
  onBack: () => void;
  onConfirm: (drafts: SuggestionDraft[]) => void;
}

const AiManagementTaskSuggestions: React.FC<AiManagementTaskSuggestionsProps> = ({
  suggestions,
  saving,
  onBack,
  onConfirm,
}) => {
  const team = useSelector<ApplicationState, AiManagementState['team']>(
    (state) => state.session.ai_management.team,
  );

  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);

  const [drafts, setDrafts] = useState<SuggestionDraft[]>(() => {
    const defaultResponsibleId = team.some((member) => member.id === profile.id)
      ? String(profile.id)
      : '';
    return suggestions.map((suggestion, index) => toDraft(suggestion, index, defaultResponsibleId));
  });
  const [editingKey, setEditingKey] = useState<string | null>(null);

  const selectedDrafts = drafts.filter((draft) => draft.selected);

  const updateDraft = (key: string, patch: Partial<SuggestionDraft>) =>
    setDrafts((items) => items.map((item) => (item.key === key ? { ...item, ...patch } : item)));

  const responsibleName = (responsibleId: string) =>
    team.find((member) => String(member.id) === responsibleId)?.name ?? 'Sem responsável';

  return (
    <Container className="d-flex flex-column flex-grow-1">
      <div className="suggestions-body flex-grow-1 px-4 pb-4">
        <div className="d-flex align-items-center mb-1">
          <i className="material-icons text-primary mr-2">smart_toy</i>
          <span className="font-weight-bold text-dark">Tarefas Sugeridas pela IA</span>
        </div>
        <p className="mb-1">
          Encontramos algumas atividades normalmente realizadas neste tipo de licitação.
        </p>
        <p className="suggestions-count text-muted mb-3">
          {selectedDrafts.length} de {drafts.length}{' '}
          {drafts.length === 1 ? 'tarefa selecionada' : 'tarefas selecionadas'}
        </p>

        {drafts.map((draft) => {
          const editing = editingKey === draft.key;

          return (
            <div key={draft.key} className="suggestion-card border rounded p-3 mb-2">
              <div className="d-flex align-items-start">
                <input
                  type="checkbox"
                  className="mt-1 mr-2 flex-shrink-0"
                  checked={draft.selected}
                  onChange={() => updateDraft(draft.key, { selected: !draft.selected })}
                  aria-label={`Selecionar ${draft.title}`}
                />
                <span className="font-weight-bold text-dark flex-grow-1">{draft.title}</span>
                <button
                  type="button"
                  className="btn btn-link p-0 ml-2 flex-shrink-0"
                  onClick={() => setEditingKey(editing ? null : draft.key)}
                >
                  {editing ? 'Salvar' : 'Editar'}
                </button>
              </div>

              {editing ? (
                <div className="suggestion-form mt-3">
                  <div className="form-group mb-2">
                    <label htmlFor={`suggestion-responsible-${draft.key}`}>Responsável</label>
                    <select
                      id={`suggestion-responsible-${draft.key}`}
                      className="form-control"
                      value={draft.responsibleId}
                      onChange={(event) =>
                        updateDraft(draft.key, { responsibleId: event.target.value })
                      }
                    >
                      <option value="">Sem responsável</option>
                      {team.map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-row">
                    <div className="form-group col-7 mb-2">
                      <label htmlFor={`suggestion-done-date-${draft.key}`}>
                        Prazo de conclusão previsto
                      </label>
                      <input
                        id={`suggestion-done-date-${draft.key}`}
                        type="date"
                        className="form-control"
                        value={draft.doneDate}
                        onChange={(event) =>
                          updateDraft(draft.key, { doneDate: event.target.value })
                        }
                      />
                    </div>
                    <div className="form-group col-5 mb-2">
                      <label htmlFor={`suggestion-done-time-${draft.key}`}>Horário</label>
                      <input
                        id={`suggestion-done-time-${draft.key}`}
                        type="time"
                        className="form-control"
                        value={draft.doneTime}
                        onChange={(event) =>
                          updateDraft(draft.key, { doneTime: event.target.value })
                        }
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group col-7 mb-2">
                      <label htmlFor={`suggestion-fatal-date-${draft.key}`}>Prazo Fatal</label>
                      <input
                        id={`suggestion-fatal-date-${draft.key}`}
                        type="date"
                        className="form-control"
                        value={draft.fatalDate}
                        disabled={draft.fatalNotApplicable}
                        onChange={(event) =>
                          updateDraft(draft.key, { fatalDate: event.target.value })
                        }
                      />
                    </div>
                    <div className="form-group col-5 mb-2">
                      <label htmlFor={`suggestion-fatal-time-${draft.key}`}>Horário</label>
                      <input
                        id={`suggestion-fatal-time-${draft.key}`}
                        type="time"
                        className="form-control"
                        value={draft.fatalTime}
                        disabled={draft.fatalNotApplicable}
                        onChange={(event) =>
                          updateDraft(draft.key, { fatalTime: event.target.value })
                        }
                      />
                    </div>
                  </div>

                  <label className="d-flex align-items-center font-weight-normal mb-0">
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={draft.fatalNotApplicable}
                      onChange={() =>
                        updateDraft(draft.key, {
                          fatalNotApplicable: !draft.fatalNotApplicable,
                        })
                      }
                    />
                    Não se aplica Prazo Fatal
                  </label>
                </div>
              ) : (
                <div className="suggestion-meta text-muted mt-2">
                  <div className="d-flex flex-wrap">
                    <span className="d-flex align-items-center mr-3">
                      <i className="material-icons mr-1">event</i>
                      Prazo de Conclusão: {formatDeadline(draft.doneDate, draft.doneTime)}
                    </span>
                    <span className="d-flex align-items-center">
                      <i className="material-icons mr-1">event_busy</i>
                      Prazo Fatal:{' '}
                      {draft.fatalNotApplicable
                        ? 'Não se aplica'
                        : formatDeadline(draft.fatalDate, draft.fatalTime)}
                    </span>
                  </div>
                  <div className="d-flex align-items-center">
                    <i className="material-icons mr-1">person</i>
                    {responsibleName(draft.responsibleId)}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="suggestions-footer d-flex align-items-center justify-content-end border-top px-4 py-3">
        <button type="button" className="btn btn-link mr-2" onClick={onBack} disabled={saving}>
          Voltar
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => onConfirm(selectedDrafts)}
          disabled={saving || selectedDrafts.length === 0}
        >
          {saving ? 'Criando...' : 'Usar sugestões'}
        </button>
      </div>
    </Container>
  );
};

export default AiManagementTaskSuggestions;
